<ng-container *transloco="let t; read: 'operations'">
  <app-wizard-modal
    #wizard
    [(open)]="open"
    [title]="mode() === 'add' ? t('profileWizard.titleAdd') : t('profileWizard.titleEdit')"
    [finishLabel]="mode() === 'add' ? t('profileWizard.finishAdd') : t('profileWizard.finishEdit')"
    [busy]="busy()"
    [confirmClose]="true"
    (finished)="onFinished()"
    (closed)="onClose()"
  >
    <!-- 1 · Basics: profile id + power mode -->
    <app-wizard-step
      id="basics"
      [label]="t('profileWizard.steps.basics')"
      [sublabel]="t('profileWizard.steps.basicsSub')"
      [description]="t('profileWizard.steps.basicsDesc')"
      icon="icon-tag"
      [control]="basicsForm"
    >
      <ng-template>
        <form [formGroup]="basicsForm" class="flex flex-col gap-6">
          <app-form-field
            [help]="t('profileWizard.fields.profileIdHelp')"
            [error]="err(basicsForm.controls.operationProfileId, t('profileWizard.errors.profileId'))"
          >
            <app-text-input
              formControlName="operationProfileId"
              [label]="t('profileWizard.fields.profileId')"
              [placeholder]="t('profileWizard.fields.profileIdPlaceholder')"
              [invalid]="isInvalid(basicsForm.controls.operationProfileId)"
            />
          </app-form-field>

          <div>
            <div class="mb-3 text-[14px] font-semibold text-ink-1">
              {{ t('profileWizard.fields.powerMode') }}
            </div>
            <div
              role="radiogroup"
              [attr.aria-label]="t('profileWizard.fields.powerMode')"
              class="flex flex-col gap-2"
            >
              @for (pm of powerModes; track pm.value) {
                <button
                  type="button"
                  role="radio"
                  [attr.aria-checked]="powerMode() === pm.value"
                  (click)="setPowerMode(pm.value)"
                  class="flex items-start gap-3 rounded-control p-4 text-left"
                  [class.shadow-hairline]="powerMode() !== pm.value"
                  [class.ring-2]="powerMode() === pm.value"
                  [class.ring-focus]="powerMode() === pm.value"
                  [class.bg-sunken]="powerMode() === pm.value"
                >
                  <span
                    class="mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-pill border"
                    [class.border-action]="powerMode() === pm.value"
                    [class.border-hairline]="powerMode() !== pm.value"
                  >
                    @if (powerMode() === pm.value) {
                      <span class="h-2 w-2 rounded-pill bg-action"></span>
                    }
                  </span>
                  <app-icon [name]="pm.icon" [size]="18" class="mt-0.5 flex-none text-ink-2" />
                  <span>
                    <span class="block text-[14px] font-semibold leading-[18px] text-ink-1">
                      {{ t('profileWizard.fields.' + pm.key) }}
                    </span>
                    <span class="block text-[12px] leading-[18px] text-ink-2">
                      {{ t('profileWizard.fields.' + pm.key + 'Desc') }}
                    </span>
                  </span>
                </button>
              }
            </div>
          </div>
        </form>
      </ng-template>
    </app-wizard-step>

    <!-- 2 · Power thresholds: battery profiles only -->
    @if (powerMode() === 'BATTERY') {
      <app-wizard-step
        id="thresholds"
        [label]="t('profileWizard.steps.thresholds')"
        [sublabel]="t('profileWizard.steps.thresholdsSub')"
        [description]="t('profileWizard.steps.thresholdsDesc')"
        icon="icon-battery-low"
        [control]="thresholdsForm"
        [error]="
          thresholdsForm.touched && thresholdErrorKey()
            ? t('profileWizard.errors.' + thresholdErrorKey())
            : null
        "
      >
        <ng-template>
          <app-threshold-editor [form]="thresholdsForm" />
          <p class="mt-6 rounded-control bg-info-surface px-4 py-3 text-[12px] leading-[18px] text-brand">
            {{ t('profileWizard.thresholdsNote') }}
          </p>
        </ng-template>
      </app-wizard-step>
    }

    <!-- 3 · Timing: intervals + wake on demand -->
    <app-wizard-step
      id="timing"
      [label]="t('profileWizard.steps.timing')"
      [sublabel]="t('profileWizard.steps.timingSub')"
      [description]="t('profileWizard.steps.timingDesc')"
      icon="icon-clock"
      [optional]="true"
      [control]="timingForm"
    >
      <ng-template>
        <form [formGroup]="timingForm" class="flex flex-col gap-6">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <app-form-field
              [help]="t('profileWizard.fields.renderIntervalHelp')"
              [error]="err(timingForm.controls.renderInterval, t('profileWizard.errors.interval'))"
            >
              <app-number-input
                formControlName="renderInterval"
                [label]="t('profileWizard.fields.renderInterval')"
                unit="s"
                [min]="0"
                [invalid]="isInvalid(timingForm.controls.renderInterval)"
              />
            </app-form-field>
            <app-form-field
              [help]="t('profileWizard.fields.connectionIntervalHelp')"
              [error]="err(timingForm.controls.connectionInterval, t('profileWizard.errors.interval'))"
            >
              <app-number-input
                formControlName="connectionInterval"
                [label]="t('profileWizard.fields.connectionInterval')"
                unit="s"
                [min]="0"
                [invalid]="isInvalid(timingForm.controls.connectionInterval)"
              />
            </app-form-field>
          </div>

          <div class="flex items-center justify-between gap-4 rounded-control p-4 shadow-hairline">
            <div>
              <div class="text-[14px] font-semibold leading-[18px] text-ink-1">
                {{ t('profileWizard.fields.allowWakeup') }}
              </div>
              <div class="text-[12px] leading-[18px] text-ink-2">
                {{ t('profileWizard.fields.allowWakeupDesc') }}
              </div>
            </div>
            <app-toggle formControlName="allowWakeup" />
          </div>
        </form>
      </ng-template>
    </app-wizard-step>

    <!-- 4 · Review -->
    <app-wizard-step
      id="review"
      [label]="t('profileWizard.steps.review')"
      [sublabel]="t('profileWizard.steps.reviewSub')"
      [description]="t('profileWizard.steps.reviewDesc')"
      icon="icon-clipboard-check"
    >
      <ng-template>
        <div class="overflow-hidden rounded-control shadow-hairline">
          <div class="p-5">
            <div class="mb-2.5 flex items-baseline justify-between">
              <span class="text-[12px] font-semibold uppercase tracking-wide text-ink-3">
                {{ t('profileWizard.steps.basics') }}
              </span>
              <button type="button" class="text-[12px] text-link hover:underline" (click)="wizard.goTo('basics')">
                {{ t('actions.edit') }}
              </button>
            </div>
            <div class="flex flex-col gap-1.5 text-[14px] leading-5">
              <div class="flex gap-4">
                <span class="w-44 flex-none text-ink-2">{{ t('profileWizard.review.profileId') }}</span>
                <span class="text-ink-1">{{ basicsForm.getRawValue().operationProfileId }}</span>
              </div>
              <div class="flex gap-4">
                <span class="w-44 flex-none text-ink-2">{{ t('profileWizard.review.powerMode') }}</span>
                <span class="text-ink-1">
                  {{ powerMode() === 'BATTERY' ? t('profileWizard.fields.battery') : t('profileWizard.fields.main') }}
                </span>
              </div>
            </div>
          </div>

          @if (powerMode() === 'BATTERY') {
            <div class="border-t border-hairline p-5">
              <div class="mb-2.5 flex items-baseline justify-between">
                <span class="text-[12px] font-semibold uppercase tracking-wide text-ink-3">
                  {{ t('profileWizard.steps.thresholds') }}
                </span>
                <button type="button" class="text-[12px] text-link hover:underline" (click)="wizard.goTo('thresholds')">
                  {{ t('actions.edit') }}
                </button>
              </div>
              <div class="flex flex-col gap-1.5 text-[14px] leading-5">
                @if (thresholdPresetKey(); as presetKey) {
                  <div class="flex gap-4">
                    <span class="w-44 flex-none text-ink-2">{{ t('profileWizard.review.preset') }}</span>
                    <span class="text-ink-1">{{ t('profileWizard.editor.presets.' + presetKey) }}</span>
                  </div>
                }
                <div class="flex gap-4">
                  <span class="w-44 flex-none text-ink-2">{{ t('profileWizard.fields.lowPower') }}</span>
                  <span class="text-ink-1">
                    {{
                      t('profileWizard.review.lowPowerValue', {
                        in: thresholdsForm.controls.lowPowerPercentage.value,
                        out: thresholdsForm.controls.lowPowerPercentageOut.value
                      })
                    }}
                  </span>
                </div>
                <div class="flex gap-4">
                  <span class="w-44 flex-none text-ink-2">{{ t('profileWizard.fields.noPower') }}</span>
                  <span class="text-ink-1">
                    {{
                      t('profileWizard.review.noPowerValue', {
                        in: thresholdsForm.controls.noPowerPercentage.value,
                        out: thresholdsForm.controls.noPowerPercentageOut.value
                      })
                    }}
                  </span>
                </div>
              </div>
            </div>
          }

          <div class="border-t border-hairline p-5">
            <div class="mb-2.5 flex items-baseline justify-between">
              <span class="text-[12px] font-semibold uppercase tracking-wide text-ink-3">
                {{ t('profileWizard.steps.timing') }}
              </span>
              <button type="button" class="text-[12px] text-link hover:underline" (click)="wizard.goTo('timing')">
                {{ t('actions.edit') }}
              </button>
            </div>
            <div class="flex flex-col gap-1.5 text-[14px] leading-5">
              <div class="flex gap-4">
                <span class="w-44 flex-none text-ink-2">{{ t('profileWizard.review.render') }}</span>
                <span class="text-ink-1">
                  @if (interval(timingForm.controls.renderInterval.value); as iv) {
                    {{ t('profileWizard.review.interval.' + iv.unit, { n: iv.n }) }}
                  }
                </span>
              </div>
              <div class="flex gap-4">
                <span class="w-44 flex-none text-ink-2">{{ t('profileWizard.review.connection') }}</span>
                <span class="text-ink-1">
                  @if (interval(timingForm.controls.connectionInterval.value); as iv) {
                    {{ t('profileWizard.review.interval.' + iv.unit, { n: iv.n }) }}
                  }
                </span>
              </div>
              <div class="flex gap-4">
                <span class="w-44 flex-none text-ink-2">{{ t('profileWizard.review.wakeup') }}</span>
                <span class="text-ink-1">
                  {{ timingForm.controls.allowWakeup.value ? t('on') : t('off') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </ng-template>
    </app-wizard-step>
  </app-wizard-modal>
</ng-container>
